import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { StakeholderTypeEnumDTO } from 'common/enums/sstk-enum';
import { ContactCustomFields } from 'common/models/ContactCustomFields';
import { CreateAutosaveContactRequest } from 'common/models/SstkAutosaveContact';
import { toIsoWithTimeZone } from 'common/utils/dateUtils';
import CONFIG from 'config';
import * as Api from '../utils/api';
import * as Auth from '../utils/authUtils';
import * as GraphApi from '../utils/graphApi';
import * as UserSettings from '../utils/user-settings';
import { clients } from './app';
import * as AppContacts from './contacts';

const TAG = 'AMG1: ';

export interface MigrationResult {
  success: boolean;
  contactsMigrated: number;
  contactsSynced: number;
  error?: string;
}

export interface MigrationState {
  hasMigrated: boolean;
}

/**
 * Service to handle migration between Outlook contact folders and database autosave contacts
 */
export class AutosaveMigrationService {
  private readonly contactFolderName: string;

  constructor() {
    this.contactFolderName = CONFIG.contactGroupName;
  }

  /**
   * Get the current migration state from database
   */
  async getMigrationState(): Promise<MigrationState> {
    try {
      const hasMigrated = await Api.isAutosaveContactMigrated();
      return {
        hasMigrated: hasMigrated || false,
      };
    } catch (error) {
      console.error(TAG, 'Failed to get migration state from database:', error);
      return {
        hasMigrated: false,
      };
    }
  }

  /**
   * Main migration orchestration method
   */
  async handleMigration(
    isNewSstkAutosaveContactEnabled: boolean
  ): Promise<MigrationResult> {
    console.log(
      TAG,
      'Starting migration orchestration, enabled:',
      isNewSstkAutosaveContactEnabled
    );

    if (!Auth.isTokenValid()) {
      await Auth.refreshToken();
    }

    const state = await this.getMigrationState();
    console.log(TAG, 'Current migration state:', state);

    if (isNewSstkAutosaveContactEnabled) {
      if (state.hasMigrated) {
        console.log(TAG, 'Autosave migration already completed.');
        return { success: true, contactsMigrated: 0, contactsSynced: 0 };
      } else {
        // First time migration
        return await this.performInitialMigration();
      }
    } else {
      // Disabled - just return success, no folder operations needed
      console.log(TAG, 'Autosave migration is disabled, no action needed');
      return { success: true, contactsMigrated: 0, contactsSynced: 0 };
    }
  }

  /**
   * Perform initial migration from Outlook folder to database
   */
  private async performInitialMigration(): Promise<MigrationResult> {
    console.log(TAG, 'Performing initial migration');

    try {
      const contacts = await this.getContactsFromFolder(
        this.contactFolderName,
        UserSettings.getDomain()
      );
      console.log(TAG, `Found ${contacts.length} contacts to migrate`);

      if (contacts.length === 0) {
        console.log(TAG, 'No contacts found to migrate');
        await Api.setAutosaveContactMigrateSuccess(); // Mark as completed even if no contacts
        return { success: true, contactsMigrated: 0, contactsSynced: 0 };
      }

      let migratedCount = 0;
      const errors: string[] = [];

      for (const contact of contacts) {
        try {
          const email = await this.migrateContactToDatabase(contact);
          if (email) {
            migratedCount++;
          }
        } catch (error) {
          const errorMsg = `Failed to migrate contact ${contact.emailAddresses?.[0]?.address}: ${error}`;
          console.error(TAG, errorMsg);
          errors.push(errorMsg);
        }
      }

      // Mark migration as completed in database
      if (migratedCount > 0) {
        try {
          await Api.setAutosaveContactMigrateSuccess();
          console.log(
            TAG,
            `Successfully migrated ${migratedCount} contacts and marked migration as completed`
          );
        } catch (stateError) {
          console.error(
            TAG,
            'Failed to mark migration as completed in database:',
            stateError
          );
          // Migration was successful but state tracking failed - don't fail the entire operation
          console.warn(
            TAG,
            'Migration completed but failed to update state in database'
          );
        }
      }

      if (errors.length > 0) {
        return {
          success: false,
          contactsMigrated: migratedCount,
          contactsSynced: 0,
          error: errors.join('; '),
        };
      }

      return {
        success: true,
        contactsMigrated: migratedCount,
        contactsSynced: 0,
      };
    } catch (error) {
      console.error(TAG, 'Initial migration failed:', error);
      return {
        success: false,
        contactsMigrated: 0,
        contactsSynced: 0,
        error: `Migration failed: ${error}`,
      };
    }
  }

  /**
   * Get contacts from the autosave folder
   */
  private async getContactsFromFolder(folderName: string, domainUrl: string) {
    const group = await GraphApi.getOrCreateContactGroup(folderName);
    const contacts = await GraphApi.getContacts(group.id);

    // Filter contacts by domainUrl in personalNotes
    return contacts.filter((contact) => {
      try {
        const customFields = AppContacts.getCustomFields(contact);
        return customFields.domainUrl === domainUrl;
      } catch {
        return false;
      }
    });
  }

  /**
   * Migrate a single Outlook contact to database
   */
  private async migrateContactToDatabase(
    contact: MicrosoftGraph.Contact
  ): Promise<string | null> {
    const email = contact.emailAddresses?.[0]?.address;
    if (!email) {
      throw new Error('Contact has no email address');
    }

    // Extract custom fields from contact
    let customFields: ContactCustomFields;
    try {
      customFields = AppContacts.getCustomFields(contact);
    } catch (error) {
      console.warn(TAG, `Failed to extract custom fields for ${email}`, error);
      console.warn(
        TAG,
        'migrating contact with no custom fields would skip',
        email
      );
      return null;
    }

    // Create autosave contact in database using extracted custom fields
    const request = this.createAutosaveContactRequest(customFields);
    await Api.saveNewAutosaveContact(request);

    console.log(TAG, `Successfully migrated contact ${email} to database`);
    return email;
  }

  private createAutosaveContactRequest(
    customFields: ContactCustomFields
  ): CreateAutosaveContactRequest {
    const clientCode = customFields.clientId || UserSettings.getClientCode();
    const clientID = clients.find((c) => c.Name === clientCode)?.ClientID || 0;

    return {
      clientID: clientID,
      projectID: customFields.projectId || UserSettings.getProject(),
      autosaveStartDate: toIsoWithTimeZone(customFields.startAutosaveDate),
      lastSyncDate: toIsoWithTimeZone(customFields.lastSyncDate),
      email: customFields.stakeholder.Email,
      firstName: customFields.stakeholder.FirstName,
      lastName: customFields.stakeholder.LastName,
      stakeholderType: customFields.stakeholder
        .StakeholderType as unknown as StakeholderTypeEnumDTO,
      confidentialityID: customFields.communicationTemplate?.Confidential?.Value
        ? +customFields.communicationTemplate.Confidential.Value
        : undefined,
      assignedToUserID: customFields.communicationTemplate?.AssignedTo?.Value
        ? +customFields.communicationTemplate.AssignedTo.Value
        : undefined,
      priorityID: customFields.communicationTemplate?.Priority?.Value
        ? +customFields.communicationTemplate.Priority.Value
        : undefined,
      statusID: customFields.communicationTemplate?.Status?.Value
        ? +customFields.communicationTemplate.Status.Value
        : undefined,
      interactionTypeID: customFields.communicationTemplate?.InteractionType
        ?.Value
        ? +customFields.communicationTemplate.InteractionType.Value
        : undefined,
      tags: customFields.communicationTemplate?.SentimentPhrases || [],
      additionalSharedProjectIDs:
        customFields.communicationTemplate?.AdditionalSharedProjectIDs || [],
    };
  }
}

// Export singleton instance
export const autosaveMigrationService = new AutosaveMigrationService();
